// Block K (UI-22): the expense form as a v2 Modal. Used by Invoicing > Expenses and by a project's
// Financials tab; both pass { expense, onSave, onClose } and render it only while it is open.
//
// onSave may return a promise: the Save button waits on it, and the caller keeps the form open on a
// failure (it simply does not close it), so what was typed is never lost.
//
// THE RECEIPT (Carla's decision 2, 2026-10-12). POST /api/expenses/receipt took the file as multipart
// and the edge refused any body over about 8 KB, so it never stored a real receipt; it is gone. The
// file is uploaded to the private file store on choosing it (POST /api/files, kind 'expense', this
// expense), and nothing is pointed at it until Save (PUT /api/expenses/:id/receipt): Cancel leaves
// the expense as it was. The store keys a file to the expense it belongs to, so a NEW expense is
// saved first and its receipt attached when it is opened again.
import { useState, useEffect, useRef } from "react";
import { EXPENSE_CATEGORIES } from "../../services/expenseService";
import { getProjects } from "../../services/projectService";
import { useContacts } from "../../contexts/ContactsContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { api } from "../../api/client";
import { describeWriteFailure } from "../../utils/writeFailure";
import { uploadStoredFile, fileTooBig } from "../../utils/storedFiles";
import { formatMoney, currencySymbol } from "../../utils/currency";
import { messageFromError } from "../../utils/apiError";
import { Modal, Button, Field, Input, Textarea, Select, MoneyInput, DateInput, Checkbox } from "../../ui";
import "./expenses.css";

const today = () => new Date().toISOString().slice(0, 10);

// A failed write, in words: the server's own sentence when it sent one; otherwise what the status
// means (a 403 with no body is the edge refusing it before it reached Aidey); otherwise the
// error's own message.
export function expenseFailure(err, what) {
  if (typeof err?.body?.error === "string" && err.body.error.trim()) return err.body.error;
  if (err?.status || err instanceof TypeError) return describeWriteFailure(err, what);
  return err?.message || describeWriteFailure(err, what);
}

const TAX_RATES = [
  { value: 0, label: "0% (No VAT)" },
  { value: 5, label: "5% (Reduced rate)" },
  { value: 20, label: "20% (Standard rate)" },
];

function ExpenseForm({ expense, onSave, onClose }) {
  // A project opens a NEW expense as { project_id }: it is an edit only when it has an id.
  const isEdit = expense?.id != null;
  const { contacts } = useContacts();
  const { currentUser } = useCurrentUser();
  // Expenses carry no currency of their own: they are in the business's base currency.
  const currency = expense?.currency || currentUser?.baseCurrency || currentUser?.currencyDisplay || "GBP";
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let alive = true;
    getProjects()
      .then((items) => {
        if (alive) setProjects(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (alive) setProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Determine if the stored category is custom
  const storedCat = expense?.category ?? "";
  const isCustom = storedCat && !EXPENSE_CATEGORIES.includes(storedCat);

  const [title, setTitle] = useState(expense?.title ?? "");
  const [amount, setAmount] = useState(expense?.amount != null ? String(expense.amount) : "");
  const [date, setDate] = useState(expense?.date ?? today());
  const [category, setCategory] = useState(isCustom ? "Custom" : storedCat);
  const [customCategory, setCustomCategory] = useState(isCustom ? storedCat : "");
  const [projectId, setProjectId] = useState(expense?.project_id ?? null);
  const [contactId, setContactId] = useState(expense?.contact_id ?? null);
  const [isBillable, setIsBillable] = useState(expense?.is_billable ?? false);
  const [isBilled, setIsBilled] = useState(expense?.is_billed ?? false);
  const [taxRate, setTaxRate] = useState(expense?.tax_rate ?? 0);
  const [notes, setNotes] = useState(expense?.notes ?? "");
  const [receiptUrl, setReceiptUrl] = useState(expense?.receipt_url ?? null);
  // undefined: the receipt is unchanged; a file id: a new one, uploaded; null: removed. Applied on Save.
  const [receiptChange, setReceiptChange] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const amountNum = parseFloat(amount) || 0;
  const vatAmount = amountNum * taxRate / 100;
  const totalIncVat = amountNum + vatAmount;

  // M2 (Aidey Intelligence, auto_categorise_expense): on a NEW expense, the category this person
  // usually files the supplier under (the contact, else the title) is pre-filled and marked as Aidey's
  // suggestion. The server decides whether there is one (GET /api/expenses/category-suggestion: the
  // automation on, the master switch on, the confidence at its threshold). Choosing a category by hand
  // ends it: a later suggestion never overrides the person's own choice.
  const [suggestion, setSuggestion] = useState(null);       // { category, count, total } shown as Aidey's
  const [suggestionError, setSuggestionError] = useState("");
  const pickedByHand = useRef(false);
  useEffect(() => {
    if (isEdit || pickedByHand.current) return undefined;
    const supplierTitle = title.trim();
    if (!contactId && !supplierTitle) return undefined;
    let alive = true;
    const timer = setTimeout(async () => {
      const q = contactId ? `contactId=${encodeURIComponent(contactId)}` : `title=${encodeURIComponent(supplierTitle)}`;
      try {
        const res = await api(`/api/expenses/category-suggestion?${q}`);
        const d = await res.json();
        if (!alive || pickedByHand.current) return;
        setSuggestionError("");
        const s = d?.suggestion;
        if (!s?.category) { setSuggestion(null); return; }
        const builtIn = EXPENSE_CATEGORIES.find((c) => c.toLowerCase() === String(s.category).toLowerCase());
        if (builtIn) { setCategory(builtIn); setCustomCategory(""); } else { setCategory("Custom"); setCustomCategory(s.category); }
        setSuggestion(s);
      } catch (err) {
        if (alive) setSuggestionError(messageFromError(err, "Aidey could not suggest a category just now."));
      }
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [isEdit, title, contactId]);

  function chooseCategory(value) {
    pickedByHand.current = true;
    setSuggestion(null);
    setCategory(value);
  }

  const resolvedCategory = category === "Custom" ? customCategory.trim() : category;
  const canSave = title.trim() && amountNum > 0 && date && resolvedCategory && !uploading && !saving;

  async function uploadReceipt(file) {
    if (!file || !isEdit) return;
    setReceiptError(null);
    const tooBig = fileTooBig(file);
    if (tooBig) { setReceiptError(tooBig); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    try {
      const stored = await uploadStoredFile({ kind: "expense", entityId: expense.id, file });
      setReceiptChange(stored.id);
      setReceiptUrl(`storedfile:${stored.id}`);
    } catch (err) {
      setReceiptError(expenseFailure(err, "upload that receipt"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      // The receipt first: the expense is saved with what the server now holds, read back.
      let savedReceipt = receiptUrl;
      if (isEdit && receiptChange !== undefined) {
        try {
          const res = await api(`/api/expenses/${expense.id}/receipt`, { method: "PUT", body: JSON.stringify({ fileId: receiptChange }) });
          const d = await res.json();
          savedReceipt = d?.expense?.receiptUrl ?? null;
          setReceiptUrl(savedReceipt);
          setReceiptChange(undefined);
        } catch (err) {
          setReceiptError(expenseFailure(err, "save that receipt"));
          return;
        }
      }
      await onSave({
        id: expense?.id,
        title: title.trim(),
        amount: amountNum,
        date,
        category: resolvedCategory,
        project_id: projectId,
        contact_id: contactId,
        receipt_url: savedReceipt,
        notes: notes.trim(),
        is_billable: isBillable,
        is_billed: isBillable ? isBilled : false,
        tax_rate: taxRate,
        created_at: expense?.created_at ?? new Date().toISOString(),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={saving ? undefined : onClose}
      title={isEdit ? "Edit expense" : "New expense"}
      color="pastel-orange"
      width={620}
      closeOnBackdrop={false}
      footer={<>
        <Button variant="tertiary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="confirm" onClick={handleSave} disabled={!canSave} aria-busy={saving || undefined}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Save expense"}
        </Button>
      </>}
    >
      <div className="exp-form">
        <Field label="Expense title" required>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Google Ads, April" data-autofocus="" />
        </Field>

        <div className="invc-form-grid">
          {/* 2026-10-20: the amount is NET (the VAT line below adds the VAT; the Tax summary
              reclaims amount x rate). Labelled only "Amount", a receipt's VAT-inclusive total went
              in and was re-billed with the VAT added again. */}
          <Field label="Amount (excluding VAT)" required hint="Before VAT. Choose the VAT rate below and the VAT is added to it.">
            <MoneyInput currency={currencySymbol(currency)} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </Field>
          <Field label="Date" required>
            <DateInput value={date} onChange={e => setDate(e.target.value)} />
          </Field>
        </div>

        <Field label="Category" required
          hint={suggestion
            ? `Aidey's suggestion: you filed ${suggestion.count} of your last ${suggestion.total} expenses from this supplier under ${suggestion.category}. Change it if this one is different.`
            : (suggestionError || undefined)}>
          <Select value={category} onChange={e => chooseCategory(e.target.value)}>
            <option value="">Select category</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            <option value="Custom">Custom…</option>
          </Select>
        </Field>
        {category === "Custom" && (
          <Field label="Custom category" required>
            <Input value={customCategory} onChange={e => { pickedByHand.current = true; setSuggestion(null); setCustomCategory(e.target.value); }} placeholder="Enter custom category" autoFocus={!suggestion} />
          </Field>
        )}

        <div className="invc-form-grid">
          <Field label="Attach to project (optional)">
            <Select
              value={projectId != null ? String(projectId) : ""}
              onChange={e => setProjectId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">No project</option>
              {projects.map(p => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}{p.status && p.status !== "active" ? ` (${p.status})` : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Supplier or contact (optional)">
            <Select value={contactId != null ? String(contactId) : ""} onChange={e => setContactId(e.target.value || null)}>
              <option value="">No supplier</option>
              {contacts.map(c => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}{c.company ? ` (${c.company})` : ""}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="invc-panel exp-checks">
          <Checkbox
            checked={isBillable}
            onChange={checked => { setIsBillable(checked); if (!checked) setIsBilled(false); }}
            label="Billable to a client"
          />
          {isBillable && (
            <div className="exp-checks__sub">
              <Checkbox checked={isBilled} onChange={setIsBilled} label="Already invoiced to the client" />
            </div>
          )}
        </div>

        <div className="exp-vat">
          <Field label="VAT rate (optional)">
            <Select value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} options={TAX_RATES} />
          </Field>
          {taxRate > 0 && amountNum > 0 && (
            <p className="exp-vat__sum">
              VAT: <strong>{formatMoney(vatAmount, currency)}</strong> · Total inc. VAT: <strong>{formatMoney(totalIncVat, currency)}</strong>
            </p>
          )}
        </div>

        <div className="ui-field">
          <span className="ui-field__label" id="exp-receipt-label">Receipt (optional)</span>
          <div className="exp-receipt">
            <input
              ref={fileRef}
              type="file"
              hidden
              aria-label="Receipt file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
              onChange={e => uploadReceipt(e.target.files?.[0])}
            />
            {!isEdit ? (
              <span className="ui-field__hint">Save the expense first, then open it again to attach its receipt.</span>
            ) : receiptUrl ? (
              <>
                <span className="exp-receipt__file">Receipt attached</span>
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>Replace</Button>
                <Button variant="tertiary" size="sm" onClick={() => { setReceiptUrl(null); setReceiptChange(null); setReceiptError(null); }} disabled={uploading}>Remove</Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                aria-describedby="exp-receipt-label" aria-busy={uploading || undefined}>
                {uploading ? "Uploading…" : "Attach receipt"}
              </Button>
            )}
          </div>
          {receiptError
            ? <span className="ui-field__error" role="alert">{receiptError}</span>
            : isEdit && <span className="ui-field__hint">A photo or a PDF, up to 10 MB.</span>}
        </div>

        <Field label="Notes (optional)">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any additional details…" />
        </Field>
      </div>
    </Modal>
  );
}

export default ExpenseForm;
